import { useMemo, useState } from "react";
import { Alert, Button, Card, Form, Input, Select, Space, Tag, message } from "antd";
import PageTitle from "../../components/PageTitle";
import ResizableTable from "../../components/ResizableTable";
import { searchPersonSplitUsers, getPersonSplitView } from "../../api";

/** 单渠道展示：order_pct / per_unit / collector / mixed / none */
function fmtChannel(ch) {
  if (!ch || ch.kind === "none" || ch.kind === "") return "—";
  if (ch.kind === "order_pct") {
    const n = Number(ch.value);
    return Number.isFinite(n) ? `${n}%` : "—";
  }
  if (ch.kind === "per_unit") {
    const n = Number(ch.value);
    const unit = ch.unit || "";
    return Number.isFinite(n) ? `${n}${unit}` : "—";
  }
  if (ch.kind === "collector") {
    const n = Number(ch.value);
    const unit = ch.unit || "";
    if (Number.isFinite(n) && unit) {
      return `收账(扣服务费${n}${unit})`;
    }
    return "收账";
  }
  if (ch.kind === "mixed" && Array.isArray(ch.parts)) {
    return ch.parts
      .map((p) => fmtChannel(p))
      .filter((s) => s && s !== "—")
      .join("+") || "—";
  }
  return "—";
}

function sideLine(block) {
  if (!block) return "—";
  return `房租 ${fmtChannel(block.rent)} · 电 ${fmtChannel(block.electric)} · 水 ${fmtChannel(block.water)}`;
}

const PersonSplitView = () => {
  const [form] = Form.useForm();
  const [searching, setSearching] = useState(false);
  const [loadingHouses, setLoadingHouses] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(undefined);
  const [userInfo, setUserInfo] = useState(null);
  const [houses, setHouses] = useState([]);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.user_id,
        label: `${u.real_name || "未命名"} / ${u.phone || "无手机"} / uid=${u.user_id}${u.huifu_id ? ` / ${u.huifu_id}` : ""}`,
      })),
    [users]
  );

  const loadHouses = async (userId) => {
    if (!userId) return;
    setLoadingHouses(true);
    try {
      const res = await getPersonSplitView({ user_id: userId });
      if (res?.success) {
        setUserInfo(res.data?.user || null);
        setHouses(Array.isArray(res.data?.houses) ? res.data.houses : []);
      } else {
        setUserInfo(null);
        setHouses([]);
        message.error(res?.error || "加载房源分账失败");
      }
    } catch (err) {
      setUserInfo(null);
      setHouses([]);
      const serverMsg = err.response?.data?.error || err.response?.data?.message;
      message.error(serverMsg ? `加载失败：${serverMsg}` : "加载房源分账失败");
    } finally {
      setLoadingHouses(false);
    }
  };

  const onSearchUsers = async () => {
    const values = form.getFieldsValue();
    const phone = String(values.phone || "").trim();
    const realName = String(values.real_name || "").trim();
    if (!phone && !realName) {
      message.warning("请至少填写手机号或姓名");
      return;
    }
    setSearching(true);
    setSelectedUserId(undefined);
    setUserInfo(null);
    setHouses([]);
    try {
      const res = await searchPersonSplitUsers({ phone, real_name: realName });
      if (!res?.success) {
        setUsers([]);
        message.error(res?.error || "搜索失败");
        return;
      }
      const list = Array.isArray(res.data?.users) ? res.data.users : [];
      setUsers(list);
      if (list.length === 0) {
        message.info("未找到匹配的汇付账号");
        return;
      }
      if (list.length === 1) {
        setSelectedUserId(list[0].user_id);
        await loadHouses(list[0].user_id);
      } else {
        message.success(`找到 ${list.length} 人，请选择后查看房源`);
      }
    } catch (err) {
      setUsers([]);
      const serverMsg = err.response?.data?.error || err.response?.data?.message;
      message.error(serverMsg ? `搜索失败：${serverMsg}` : "搜索失败");
    } finally {
      setSearching(false);
    }
  };

  const columns = [
    {
      title: "房源",
      key: "house",
      width: 220,
      render: (_, r) => (
        <div>
          <div>#{r.house_id} {r.house_name || ""}</div>
          <div style={{ color: "#888", fontSize: 12 }}>{r.address || ""}</div>
        </div>
      ),
    },
    {
      title: "角色",
      key: "roles",
      width: 140,
      render: (_, r) => (
        <Space wrap size={[4, 4]}>
          {r.is_collector ? <Tag color="blue">房东分账</Tag> : null}
          {r.is_business ? <Tag color="green">业务{r.business_slot || ""}</Tag> : null}
          {r.split_all_to_landlord ? <Tag>全额进收账</Tag> : null}
        </Space>
      ),
    },
    {
      title: "房东分账",
      key: "landlord",
      width: 260,
      render: (_, r) => (r.is_collector ? sideLine(r.landlord) : "无"),
    },
    {
      title: "业务分账",
      key: "business",
      width: 260,
      render: (_, r) => (r.is_business ? sideLine(r.business) : "无"),
    },
    {
      title: "合计",
      key: "total",
      width: 280,
      render: (_, r) => sideLine(r.total),
    },
  ];

  return (
    <div>
      <PageTitle title="按人查分账" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="按汇付手机号/姓名查找指定收账人与分账人A/B/C。房租与百分比水电为占整单%；定额水电：业务显示元/度·吨，房东显示「收账(扣服务费X元/度·吨)」，不按各表电价折占整单。全额进收账人按实际：房东100%、业务0。只读。"
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={onSearchUsers}>
          <Form.Item name="phone" label="汇付手机号">
            <Input allowClear placeholder="支持模糊" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="real_name" label="汇付姓名">
            <Input allowClear placeholder="支持模糊" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={searching}>
              搜索账号
            </Button>
          </Form.Item>
        </Form>
        {users.length > 1 ? (
          <div style={{ marginTop: 12 }}>
            <Space>
              <span>选择用户：</span>
              <Select
                style={{ minWidth: 420 }}
                options={userOptions}
                value={selectedUserId}
                placeholder="请选择"
                onChange={(v) => {
                  setSelectedUserId(v);
                  loadHouses(v);
                }}
              />
            </Space>
          </div>
        ) : null}
        {userInfo ? (
          <div style={{ marginTop: 12, color: "#555" }}>
            当前：{userInfo.real_name || "—"} / {userInfo.phone || "—"} / uid={userInfo.user_id}
            {userInfo.huifu_id ? ` / ${userInfo.huifu_id}` : ""}
          </div>
        ) : null}
        {userInfo ? (
          <div style={{ marginTop: 8, fontSize: 15, fontWeight: 600, color: "#1677ff" }}>
            搜索结果：共 {houses.length} 套房源
            {houses.length > 0
              ? `（房东分账 ${houses.filter((h) => h.is_collector).length} · 业务分账 ${houses.filter((h) => h.is_business).length} · 两者皆有 ${houses.filter((h) => h.is_collector && h.is_business).length}）`
              : ""}
          </div>
        ) : null}
      </Card>
      <ResizableTable
        rowKey="house_id"
        loading={loadingHouses}
        columns={columns}
        dataSource={houses}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 套房源`,
        }}
        scroll={{ x: 1100 }}
      />
    </div>
  );
};

export default PersonSplitView;
